import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './badges-list.scss';
import { Tooltip, Badge, FlexRow } from '@epam/promo';
import useWindowSize from '../hooks/use-window-size';

type Props = {
  items: string[];
  color: 'green' | 'blue' | 'amber';
  className?: string;
  showAllItems?: boolean; // If true, always show all items without "+X" counter
};

const BADGE_MARGIN_RIGHT = 8;

/*
 * Estimated length of an +X badge
 * */
const COUNTER_BADGE_LENGTH = 30;

const getDisplayedTechIndex = (
  containerWidth: number,
  badgesLengthList: number[]
) => {
  let sumOfBadgesLength = 0;

  for (let i = 0; i < badgesLengthList.length; i++) {
    const margin = i === badgesLengthList.length - 1 ? 0 : BADGE_MARGIN_RIGHT;
    const width = badgesLengthList[i] + margin;

    if (sumOfBadgesLength + width > containerWidth) {
      return i;
    } else {
      sumOfBadgesLength += width;
    }
  }

  return badgesLengthList.length;
};

export const BadgesList: FC<Props> = ({
  className = '',
  items,
  color,
  showAllItems = false,
}) => {
  const { width } = useWindowSize();
  const ref = useRef<HTMLDivElement>(null);
  const badgesLengthList = useRef<number[]>([]);

  const [displayedIndex, setIndex] = useState<number>(items.length);

  const calculateDisplayedIndex = () => {
    // If showAllItems is true, always display all items
    if (showAllItems) {
      setIndex(items.length);
      return;
    }

    const containerWidth = ref.current?.getBoundingClientRect().width;
    if (!containerWidth || badgesLengthList.current.length === 0) {
      setIndex(items.length);
      return;
    }

    // Step 1: Check how many fit without reserving "+count"
    const idx = getDisplayedTechIndex(containerWidth, badgesLengthList.current);

    // Step 2: If not all fit, recalc with reserved space for "+count" badge
    if (idx < badgesLengthList.current.length) {
      const adjustedIdx = getDisplayedTechIndex(
        containerWidth - COUNTER_BADGE_LENGTH - BADGE_MARGIN_RIGHT,
        badgesLengthList.current
      );
      setIndex(Math.max(1, adjustedIdx));
    } else {
      setIndex(idx);
    }
  };

  useEffect(() => {
    if (showAllItems) return;

    // Add a small delay to ensure container has settled
    const timer = setTimeout(() => {
      calculateDisplayedIndex();
    }, 50);

    return () => clearTimeout(timer);
  }, [width, showAllItems]);

  useLayoutEffect(() => {
    // If showAllItems is true, always show all items
    if (showAllItems) {
      setIndex(items.length);
      return;
    }

    if (!ref.current) return;

    // Wait until DOM + styles + fonts have settled
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Query the measurement container for badge widths
        const measurementContainer = ref.current
          ?.previousSibling as HTMLElement;
        const badges = measurementContainer?.querySelectorAll('.badge');
        if (!badges || badges.length === 0) return;

        // 1. recalc all widths from the measurement container
        badgesLengthList.current = [];
        badges.forEach((badge) => {
          const width = badge.getBoundingClientRect().width;
          badgesLengthList.current.push(width);
        });

        const containerWidth = ref.current?.getBoundingClientRect().width || 0;

        // Guard against invalid container width
        if (containerWidth === 0) {
          setIndex(items.length);
          return;
        }

        // 2. calculate how many fit without "+count"
        let idx = getDisplayedTechIndex(
          containerWidth,
          badgesLengthList.current
        );

        // 3. if not all fit, reserve space for "+count"
        if (idx < badgesLengthList.current.length) {
          idx = getDisplayedTechIndex(
            containerWidth - COUNTER_BADGE_LENGTH - BADGE_MARGIN_RIGHT,
            badgesLengthList.current
          );
          // Ensure we show at least one badge
          idx = Math.max(1, idx);
        }

        setIndex(idx);
      });
    });
  }, [items, width, showAllItems]);

  return (
    <div
      className={`badges-list__wrapper ${
        showAllItems ? 'badges-list__wrapper--show-all' : ''
      }`}
    >
      {/* Hidden measurement container - render all badges to measure them */}
      <div className="badges-list__measurement">
        <FlexRow cx={`${className} badges-measurement`}>
          {items.map((item) => (
            <Badge
              key={item}
              color={color}
              fill={'semitransparent'}
              size="18"
              caption={item}
              cx={'badge'}
              captionCX={'badge__caption'}
            />
          ))}
        </FlexRow>
      </div>

      {/* Visible container - render only what fits */}
      <FlexRow cx={className} ref={ref}>
        {items.slice(0, displayedIndex).map((item) => (
          <Badge
            key={item}
            color={color}
            fill={'semitransparent'}
            size="18"
            caption={item}
            cx={'badge'}
            captionCX={'badge__caption'}
          />
        ))}

        {displayedIndex < items.length && (
          <Tooltip content={items.slice(displayedIndex).join(', ')}>
            <Badge
              color={color}
              fill={'semitransparent'}
              size="18"
              caption={`+${items.length - displayedIndex}`}
              cx={'badge'}
              captionCX={'badge__caption'}
            />
          </Tooltip>
        )}
      </FlexRow>
    </div>
  );
};
